import { Injectable, inject } from '@angular/core';
import { ApiException, Client } from '@api/client';
import { DisputeConfirmationPopupComponent } from '@modals/dispute-confirmation-popup/dispute-confirmation-popup.component';
import { AuthService } from '@services/auth.service';
import { LoadingService } from '@services/loading.service';
import { ToastService } from '@services/toast.service';
import { AppNavigationService, StateOverlayService } from '@state-routing';
import { ModalController, ModalOptions } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

export type DisputeAssignmentResult = 'assigned' | 'cancelled' | 'failed' | 'unavailable' | 'wallet-required';

@Injectable({providedIn: 'root'})
export class DisputeAssignmentService {
  private readonly client = inject(Client);
  private readonly authService = inject(AuthService);
  private readonly loadingService = inject(LoadingService);
  private readonly modalController = inject(ModalController);
  private readonly stateOverlay = inject(StateOverlayService);
  private readonly appNavigation = inject(AppNavigationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  public async confirmAndAssign(replyId: number, taskId: number): Promise<DisputeAssignmentResult> {
    const options: ModalOptions = {
      component: DisputeConfirmationPopupComponent,
      cssClass: 'dispute-confirmation',
    };
    const modal = await this.modalController.create(options);

    await modal.present();
    const event = await modal.onDidDismiss();
    await this.stateOverlay.waitUntilSettled(modal);
    if (!event.data?.isConfirmed) {
      return 'cancelled';
    }

    try {
      await this.loadingService.start();
      await firstValueFrom(this.client.replyResolve(replyId));
      await this.authService.refreshToken();
      await this.appNavigation.navigateForward(['/tasks/dispute', taskId, 'details']);

      return 'assigned';
    } catch (error) {
      const errorCode = this.errorCode(error);
      if (errorCode === 'pjm_escrow_arbitrator_wallet_not_linked') {
        await this.appNavigation.navigateForward('/wallet');
        return 'wallet-required';
      }

      if (errorCode === 'pjm_escrow_arbitrator_funds_unavailable') {
        await this.toastService.warning(this.translate.instant('tasks.disputeFundsUnavailable'));
        return 'failed';
      }

      if (this.isUnavailable(error)) {
        await this.toastService.warning(this.translate.instant('tasks.disputeUnavailable'));
        return 'unavailable';
      }

      console.error(error);
      await this.toastService.error(this.translate.instant('tasks.disputeAssignmentFailed'));
      return 'failed';
    } finally {
      await this.loadingService.stop();
    }
  }

  private errorCode(error: unknown): string | null {
    if (!error || !ApiException.isApiException(error)) {
      return null;
    }

    if (typeof error.result?.error === 'string') {
      return error.result.error;
    }

    if (typeof error.response !== 'string') {
      return null;
    }

    try {
      return JSON.parse(error.response)?.error ?? null;
    } catch {
      return null;
    }
  }

  private isUnavailable(error: unknown): boolean {
    return !!error
      && ApiException.isApiException(error)
      && (error.status === 403 || error.status === 409);
  }
}
